/**
 * Common Utility Functions for VerveStacks Dashboard
 * 
 * This module provides general-purpose JavaScript utilities for:
 * - Function optimization (debounce, throttle)
 * - Performance optimization helpers
 * - Common UI helpers
 * 
 * These utilities help improve application performance by controlling
 * how frequently functions are executed, especially useful for:
 * - Input field handlers
 * - Scroll event handlers
 * - Window resize handlers
 * - API calls triggered by user actions
 */

/**
 * Debounce Function
 * 
 * Creates a debounced version of a function that delays its execution until
 * after a specified time has passed since the last time it was invoked.
 * 
 * This is ideal for scenarios where you want to wait until the user has
 * "finished" an action before executing the function (e.g., waiting until
 * user stops typing before making an API call).
 * 
 * @param {Function} func - The function to debounce
 * @param {number} delay - Time to wait in milliseconds (default: 500ms)
 * @returns {Function} Debounced version of the function with a cancel method
 * 
 * @example
 * // Basic usage with default 500ms delay
 * const debouncedSave = debounce((data) => {
 *   saveToDatabase(data);
 * });
 * 
 * @example
 * // Custom delay (300ms)
 * const debouncedSearch = debounce((query) => {
 *   performSearch(query);
 * }, 300);
 * 
 * // Use in event handler
 * input.addEventListener('input', (e) => {
 *   debouncedSearch(e.target.value);
 * });
 * 
 * @example
 * // Cancel pending execution
 * const debouncedFn = debounce(myFunction, 1000);
 * debouncedFn(); // Will execute after 1000ms
 * debouncedFn.cancel(); // Cancels the pending execution
 * 
 * @example
 * // In React component
 * const handleInputChange = debounce((value) => {
 *   fetchSuggestions(value);
 * }, 500);
 * 
 * return (
 *   <input onChange={(e) => handleInputChange(e.target.value)} />
 * );
 */
export function debounce(func, delay = 500) {
  // Validate inputs
  if (typeof func !== 'function') {
    throw new TypeError('First argument must be a function');
  }
  
  if (typeof delay !== 'number' || delay < 0) {
    throw new TypeError('Delay must be a non-negative number');
  }
  
  let timeoutId = null;
  
  // The debounced function
  const debouncedFunction = function(...args) {
    // Clear any existing timeout
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
    }
    
    // Set new timeout
    timeoutId = setTimeout(() => {
      timeoutId = null;
      func.apply(this, args);
    }, delay);
  };
  
  // Add cancel method to allow manual cancellation
  debouncedFunction.cancel = function() {
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  };
  
  return debouncedFunction;
}

/**
 * Throttle Function
 * 
 * Creates a throttled version of a function that will only execute at most
 * once per specified time period, regardless of how many times it's called.
 * 
 * This is ideal for scenarios where you want to limit the rate of execution
 * while still providing regular updates (e.g., scroll handlers, mouse move,
 * window resize).
 * 
 * The first call executes immediately, then subsequent calls are ignored
 * until the delay period has passed.
 * 
 * @param {Function} func - The function to throttle
 * @param {number} delay - Minimum time between executions in milliseconds (default: 500ms)
 * @returns {Function} Throttled version of the function with a cancel method
 * 
 * @example
 * // Basic usage with default 500ms throttle
 * const throttledUpdate = throttle(() => {
 *   updateUI();
 * });
 * 
 * @example
 * // Custom throttle (200ms)
 * const throttledScroll = throttle(() => {
 *   console.log('Scroll position:', window.scrollY);
 * }, 200);
 * 
 * // Use in event handler
 * window.addEventListener('scroll', throttledScroll);
 * 
 * @example
 * // Throttle window resize
 * const handleResize = throttle(() => {
 *   adjustLayout();
 * }, 300);
 * 
 * window.addEventListener('resize', handleResize);
 * 
 * @example
 * // Cancel throttled function
 * const throttledFn = throttle(myFunction, 1000);
 * throttledFn(); // Executes immediately
 * throttledFn(); // Ignored (within throttle period)
 * throttledFn.cancel(); // Cancels any pending execution
 */
export function throttle(func, delay = 500) {
  // Validate inputs
  if (typeof func !== 'function') {
    throw new TypeError('First argument must be a function');
  }
  
  if (typeof delay !== 'number' || delay < 0) {
    throw new TypeError('Delay must be a non-negative number');
  }
  
  let lastCallTime = 0;
  let timeoutId = null;
  
  // The throttled function
  const throttledFunction = function(...args) {
    const now = Date.now();
    const timeSinceLastCall = now - lastCallTime;
    
    // Clear any pending timeout
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
    
    if (timeSinceLastCall >= delay) {
      // Enough time has passed, execute immediately
      lastCallTime = now;
      func.apply(this, args);
    } else {
      // Too soon, schedule for later (trailing edge)
      const remainingTime = delay - timeSinceLastCall;
      timeoutId = setTimeout(() => {
        lastCallTime = Date.now();
        timeoutId = null;
        func.apply(this, args);
      }, remainingTime);
    }
  };
  
  // Add cancel method to allow manual cancellation
  throttledFunction.cancel = function() {
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
    lastCallTime = 0;
  };
  
  return throttledFunction;
}

/**
 * Deep Clone Object/Array
 * 
 * Creates a deep copy of an object or array, breaking all references.
 * Useful for state management and avoiding mutation issues.
 * 
 * Note: This uses JSON serialization, so it won't preserve:
 * - Functions
 * - undefined values
 * - Symbols
 * - Dates (will be converted to strings)
 * - RegExp, Map, Set, etc.
 * 
 * @param {*} obj - The object or array to clone
 * @returns {*} Deep cloned copy
 * 
 * @example
 * const original = { a: 1, b: { c: 2 } };
 * const copy = deepClone(original);
 * copy.b.c = 3;
 * console.log(original.b.c); // Still 2
 */
export function deepClone(obj) {
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }
  
  try {
    return JSON.parse(JSON.stringify(obj));
  } catch (error) {
    console.error('Deep clone failed:', error);
    return obj;
  }
}

/**
 * Check if value is empty
 * 
 * Checks if a value is empty (null, undefined, empty string, empty array, empty object)
 * 
 * @param {*} value - Value to check
 * @returns {boolean} True if empty, false otherwise
 * 
 * @example
 * isEmpty(null)        // true
 * isEmpty(undefined)   // true
 * isEmpty('')          // true
 * isEmpty([])          // true
 * isEmpty({})          // true
 * isEmpty(0)           // false
 * isEmpty('hello')     // false
 * isEmpty([1, 2])      // false
 */
export function isEmpty(value) {
  if (value === null || value === undefined) {
    return true;
  }
  
  if (typeof value === 'string') {
    return value.trim() === '';
  }
  
  if (Array.isArray(value)) {
    return value.length === 0;
  }
  
  if (typeof value === 'object') {
    return Object.keys(value).length === 0;
  }
  
  return false;
}
